import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useParams, usePathname, useRouter } from '@/routing/navigation'
import { Skeleton } from '@/components/ui/skeleton'
import { ImportWizardShell } from '../components/ImportWizardShell'
import { ImportWizardProvider } from '../context/ImportWizardContext'
import { useFlowSnapshot } from '../hooks/useFlowSnapshot'
import { useImportSessionRestoreBeacon } from '../hooks/useImportSessionRestoreBeacon'
import {
  buildImportSteps,
  getStepRouteFallback,
  isWizardInternalRoute,
  stepKeyFromPathname,
  stepKeyFromRoute,
} from '@/layouts/AudienceImportSessionLayout/lib/build-import-steps'
import {
  completeImportSessionRouteTransition,
  startImportSessionRouteTransition,
} from '../lib/import-session-client-metrics'
import type { ImportStepDefinition, ImportStepKey } from '@/types/components/audiences/import-shell'
import type { ImportWizardContextValue } from '@/types/components/audiences/import-wizard-context'
import {
  WorkbookAssignmentStep,
  ContextMaterialsStep,
  RequiredFieldsStep,
  SegmentationStep,
  ConfirmationStep,
  StatusStep,
} from './steps'

const STEP_HEADERS: Partial<Record<ImportStepKey, { title: string; description: string }>> = {
}

/**
 * Single-page wizard layout for audience-import sessions.
 *
 * All wizard steps render inline — the active step is controlled by local
 * state (`activeStepKey`) synced to the backend snapshot route. Clicking
 * the step indicator switches steps without full-page navigation.
 *
 * `{children}` is kept for route-layout compatibility but is not rendered;
 * step content is produced by the step components imported above.
 */
export default function ImportSessionLayout({
  children: _children,
}: {
  children?: React.ReactNode
}) {
  const params = useParams<{ sessionId: string }>()
  const sessionId = Array.isArray(params?.sessionId)
    ? params.sessionId[0]!
    : params?.sessionId ?? ''
  const pathname = usePathname()
  const router = useRouter()

  const pathStepKey = useMemo(() => stepKeyFromPathname(pathname), [pathname])

  const [activeStepKey, setActiveStepKey] = useState<ImportStepKey>(() =>
    stepKeyFromPathname(pathname),
  )
  const [manualSuppressRequiredFieldsRedirect, setSuppressRequiredFieldsRedirect] =
    useState(false)
  const suppressRequiredFieldsRedirect = manualSuppressRequiredFieldsRedirect

  const [requiredFieldsRedirectHold, setRequiredFieldsRedirectHold] = useState<{
    targetRoute: string
    minMachineVersion: number
  } | null>(null)

  const [hiddenFilterColumns, setHiddenFilterColumns] = useState<string[]>([])
  const [customFilterLabels, setCustomFilterLabels] = useState<
    Record<string, string>
  >({})
  const [audienceTags, setAudienceTags] = useState<
    import('@digital-twins/shared-types').AudienceTag[]
  >([])
  const lastMutationRequestIdRef = useRef<string | null>(null)
  const setLastMutationRequestId = useCallback((requestId: string | null) => {
    lastMutationRequestIdRef.current = requestId
  }, [])

  const {
    snapshot,
    error: snapshotError,
    isLoading: isSnapshotLoading,
    isValidating: isSnapshotValidating,
    mutate: mutateSnapshot,
    isRealtimeConnected,
    registerImportSessionEventHandler,
  } = useFlowSnapshot(sessionId, {
    lastMutationRequestIdRef,
  })

  const wizardPhase = snapshot?.wizardPhase ?? null
  const effectiveStepKey = activeStepKey

  const importSteps = useMemo(
    () => (snapshot ? buildImportSteps(snapshot, effectiveStepKey) : []),
    [snapshot, effectiveStepKey],
  )

  useImportSessionRestoreBeacon({
    sessionId: sessionId || null,
    route: snapshot?.route ?? null,
    machineState: snapshot?.machineState ?? null,
  })

  useEffect(() => {
    if (!sessionId) return
    completeImportSessionRouteTransition({
      sessionId,
      currentRoute: pathname,
    })
    if (manualSuppressRequiredFieldsRedirect) {
      setSuppressRequiredFieldsRedirect(false)
    }
  }, [
    manualSuppressRequiredFieldsRedirect,
    pathname,
    sessionId,
    activeStepKey,
  ])

  // Keep URL-derived step in sync before snapshot sync effects run (useEffect).
  // Otherwise snapshot redirect logic can see a stale activeStepKey and overwrite
  // the step while the pathname already points at segmentation (blank main area).
  useLayoutEffect(() => {
    setActiveStepKey(pathStepKey)
  }, [pathStepKey])

  useEffect(() => {
    setSuppressRequiredFieldsRedirect(false)
    setHiddenFilterColumns([])
    setCustomFilterLabels({})
    setAudienceTags([])
  }, [sessionId])

  const snapshotMachineVersion = snapshot?.machineVersion ?? 0
  const requiredFieldsRoute = snapshot?.links?.requiredFields?.trim() ?? null
  const stepRouteByKey = useMemo<Record<ImportStepKey, string | null>>(
    () => ({
      upload: null,
      workbook: null,
      context: snapshot?.links?.contextMaterials?.trim() ?? null,
      identity: snapshot?.links?.requiredFields?.trim() ?? null,
      segmentation: snapshot?.links?.segmentation?.trim() ?? null,
      confirm: null,
      creating: snapshot?.links?.status?.trim() ?? null,
    }),
    [
      snapshot?.links?.contextMaterials,
      snapshot?.links?.requiredFields,
      snapshot?.links?.segmentation,
      snapshot?.links?.status,
    ],
  )

  const resolveWizardStepRoute = useCallback(
    (key: ImportStepKey, explicitRoute?: string | null) => {
      const route =
        explicitRoute?.trim() ??
        stepRouteByKey[key] ??
        getStepRouteFallback(sessionId, key)
      return route && route.length > 0 ? route : null
    },
    [sessionId, stepRouteByKey],
  )

  const syncWizardStepToRoute = useCallback(
    (key: ImportStepKey, explicitRoute?: string | null) => {
      const route = resolveWizardStepRoute(key, explicitRoute)
      setActiveStepKey(key)
      if (route && route !== pathname) {
        router.replace(route)
      }
    },
    [pathname, resolveWizardStepRoute, router],
  )

  const sessionNotFound =
    !isSnapshotLoading && !snapshot && !snapshotError && !!sessionId

  useEffect(() => {
    if (sessionNotFound) {
      router.replace('/audiences/new')
    }
  }, [sessionNotFound, router])

  useEffect(() => {
    const targetRoute = snapshot?.route?.trim() ?? null
    if (!targetRoute) return
    if (!isWizardInternalRoute(targetRoute)) return

    if (
      suppressRequiredFieldsRedirect &&
      requiredFieldsRoute &&
      targetRoute === requiredFieldsRoute
    ) {
      return
    }

    if (
      requiredFieldsRedirectHold &&
      requiredFieldsRoute &&
      targetRoute === requiredFieldsRoute &&
      snapshotMachineVersion < requiredFieldsRedirectHold.minMachineVersion
    ) {
      return
    }

    if (pathStepKey === 'context') {
      return
    }

    if (pathStepKey === 'identity') {
      return
    }

    // Confirmation is a client-only step (no backend route) — skip snapshot
    // sync unless the session has moved to a terminal/creating state.
    if (pathStepKey === 'confirm') {
      const shouldAdvanceToStatus =
        snapshot?.machineState === 'creating' ||
        snapshot?.machineState === 'completed' ||
        snapshot?.machineState === 'failed_terminal' ||
        wizardPhase === 'create_running' ||
        wizardPhase === 'create_failed' ||
        wizardPhase === 'completed'
      if (!shouldAdvanceToStatus) {
        return
      }
    }

    const resolved = stepKeyFromRoute(targetRoute)

    // Do not sync the wizard onto later steps while dataset preflight is still
    // running (stale client cache or edge routes should not skip the
    // preparing-dataset interstitial).
    if (
      snapshot?.preflightStatus === 'pending' &&
      (resolved === 'segmentation' || resolved === 'context')
    ) {
      return
    }

    // Client navigation (e.g. "Set segmentation") can advance the pathname
    // before snapshot.route catches up. Skip snapshot sync while on /segmentation
    // unless audience creation has started — then we must advance to the status step.
    // When the session reaches a terminal wizard state, sync so the user is not stuck
    // on /segmentation after create completes (snapshot.route may point at status/preview).
    if (pathStepKey === 'segmentation') {
      const shouldAdvanceToStatus =
        resolved === 'creating' ||
        wizardPhase === 'create_running' ||
        wizardPhase === 'create_failed' ||
        snapshot?.machineState === 'creating'
      const shouldSyncAfterTerminal =
        snapshot?.machineState === 'completed' ||
        snapshot?.machineState === 'failed_terminal' ||
        wizardPhase === 'completed'
      if (!shouldAdvanceToStatus && !shouldSyncAfterTerminal) {
        return
      }
    }

    if (isSnapshotValidating) {
      const allowWhileValidating =
        pathStepKey === 'segmentation' &&
        (resolved === 'creating' ||
          wizardPhase === 'create_running' ||
          wizardPhase === 'create_failed' ||
          snapshot?.machineState === 'creating' ||
          snapshot?.machineState === 'completed' ||
          snapshot?.machineState === 'failed_terminal' ||
          wizardPhase === 'completed')
      if (!allowWhileValidating) {
        return
      }
    }
    if (resolved && resolved !== activeStepKey) {
      if (sessionId) {
        startImportSessionRouteTransition({
          sessionId,
          sourceRoute: pathname,
          targetRoute,
          trigger: 'snapshot_redirect',
        })
      }
      setActiveStepKey(resolved)
    }
  }, [
    activeStepKey,
    isSnapshotValidating,
    pathname,
    requiredFieldsRedirectHold,
    requiredFieldsRoute,
    sessionId,
    snapshot?.links.requiredFields,
    snapshot?.links.status,
    snapshot?.route,
    suppressRequiredFieldsRedirect,
    wizardPhase,
    snapshot?.branchSummaries?.identity?.state,
    snapshot?.machineState,
    snapshot?.preflightStatus,
    pathStepKey,
  ])

  const goToStep = useCallback(
    (key: ImportStepKey) => {
      const route = resolveWizardStepRoute(key)
      if (route) {
        router.push(route)
        return
      }
      setActiveStepKey(key)
    },
    [resolveWizardStepRoute, router],
  )

  const navigateToStep = useCallback(
    (step: ImportStepDefinition) => {
      const route = resolveWizardStepRoute(step.key, step.route)
      if (route) {
        router.push(route)
        return
      }
      setActiveStepKey(step.key)
    },
    [resolveWizardStepRoute, router],
  )

  const handleStepNavigate = useCallback(
    (idx: number) => {
      const step = importSteps[idx]
      if (!step) return
      const route = resolveWizardStepRoute(step.key, step.route)
      if (route) {
        router.push(route)
        return
      }
      setActiveStepKey(step.key)
    },
    [importSteps, resolveWizardStepRoute, router],
  )

  const contextValue: ImportWizardContextValue = useMemo(
    () => ({
      sessionId,
      snapshot,
      isSnapshotLoading,
      isSnapshotValidating,
      snapshotError,
      mutateSnapshot,
      isRealtimeConnected,
      registerImportSessionEventHandler,
      importSteps,
      navigateToStep,
      activeStepKey,
      goToStep,
      suppressRequiredFieldsRedirect,
      setSuppressRequiredFieldsRedirect,
      setRequiredFieldsRedirectHold,
      setLastMutationRequestId,
      hiddenFilterColumns,
      setHiddenFilterColumns,
      customFilterLabels,
      setCustomFilterLabels,
      audienceTags,
      setAudienceTags,
    }),
    [
      sessionId,
      snapshot,
      isSnapshotLoading,
      isSnapshotValidating,
      snapshotError,
      mutateSnapshot,
      isRealtimeConnected,
      registerImportSessionEventHandler,
      importSteps,
      navigateToStep,
      activeStepKey,
      goToStep,
      suppressRequiredFieldsRedirect,
      setLastMutationRequestId,
      hiddenFilterColumns,
      customFilterLabels,
      audienceTags,
    ],
  )

  if (isSnapshotLoading && !snapshot) {
    return (
      <ImportWizardShell>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-7 w-64 mx-auto" />
        <Skeleton className="h-5 w-80" />
        <Skeleton className="h-[300px] rounded-2xl" />
      </ImportWizardShell>
    )
  }

  if (sessionNotFound) {
    return (
      <ImportWizardShell>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-7 w-64 mx-auto" />
      </ImportWizardShell>
    )
  }

  const showStepRail = importSteps.length > 0
  const stepHeader = effectiveStepKey
    ? STEP_HEADERS[effectiveStepKey]
    : undefined

  return (
    <ImportWizardProvider value={contextValue}>
      <ImportWizardShell
        steps={showStepRail ? importSteps : undefined}
        onStepNavigate={showStepRail ? handleStepNavigate : undefined}
        title={stepHeader?.title}
        description={stepHeader?.description}
      >
        {activeStepKey === 'workbook' && <WorkbookAssignmentStep />}
        {activeStepKey === 'context' && <ContextMaterialsStep />}
        {activeStepKey === 'identity' && <RequiredFieldsStep />}
        {activeStepKey === 'segmentation' && <SegmentationStep />}
        {activeStepKey === 'confirm' && <ConfirmationStep />}
        {activeStepKey === 'creating' && <StatusStep />}
      </ImportWizardShell>
    </ImportWizardProvider>
  )
}
